import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * Text that fits its box on the TV display instead of being cut off.
 * The box is the element's width (set by the layout) and `height` px
 * (default: one line at `max`). Starting at `max`, the text shrinks a
 * pixel at a time until it fits; because the height stays fixed, it
 * shrinks on one line first and wraps onto a second only once it's
 * small enough for two lines to fit the same box. So a slide's rows
 * never grow, and nothing goes below `min`, which keeps it readable
 * across a room. Text that still won't fit at `min` switches to
 * `fallback` when there is one ("3 readers tied" for a list of names),
 * else wraps there, clamped to `lines`, as a last resort.
 *
 * Measured in the stage's own 1920×1080 pixels: the stage's scale
 * transform doesn't change layout sizes. Finds the size by binary search
 * (a handful of layouts, not one per pixel), and only when the text, the
 * box's width or the web fonts change — smart-TV browsers are slow.
 */
export default function FitText({ as: Tag = "p", max, min, height, lines = 2, lineHeight = 1.15, fallback, className = "", children }) {
  const ref = useRef(null);
  const budget = height ?? Math.ceil(max * lineHeight);
  // Set when `children` didn't fit; cleared when they change.
  const [shortened, setShortened] = useState(false);
  const content = shortened && fallback ? fallback : children;
  // What the last fit was for: text, box width and limits.
  const fitted = useRef(null);

  const fit = useCallback((force = false) => {
    const el = ref.current;
    if (!el) return;
    const signature = [el.textContent, el.clientWidth, max, min, budget, lines, lineHeight].join("|");
    if (!force && fitted.current === signature) return;
    fitted.current = signature;
    Object.assign(el.style, { display: "", WebkitLineClamp: "", WebkitBoxOrient: "", overflow: "", overflowWrap: "normal" });

    const fitsAt = (size) => {
      el.style.fontSize = `${size}px`;
      const lineCount = Math.round(el.scrollHeight / (size * lineHeight));
      return el.scrollHeight <= budget + 1 && el.scrollWidth <= el.clientWidth + 1 && lineCount <= lines;
    };
    if (fitsAt(max)) return;

    // Smaller text never needs more room, so the largest size that fits
    // can be found by halving.
    let best = null;
    for (let low = min, high = max - 1; low <= high; ) {
      const mid = Math.floor((low + high) / 2);
      if (fitsAt(mid)) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    if (best !== null) {
      el.style.fontSize = `${best}px`;
      return;
    }

    if (fallback && !shortened) {
      setShortened(true);
      return;
    }
    el.style.fontSize = `${min}px`;
    Object.assign(el.style, { display: "-webkit-box", WebkitLineClamp: String(lines), WebkitBoxOrient: "vertical", overflow: "hidden", overflowWrap: "anywhere" });
  }, [max, min, lines, budget, lineHeight, fallback, shortened]);

  useIsomorphicLayoutEffect(() => {
    setShortened(false);
  }, [children]);

  // Every render, but a no-op unless the text or width changed since the
  // last fit (children that are JSX are a new object each render).
  useIsomorphicLayoutEffect(() => {
    fit();
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let width = el.clientWidth;
    const observer = new ResizeObserver(() => {
      if (el.clientWidth === width) return;
      width = el.clientWidth;
      fit();
    });
    observer.observe(el);
    // Web fonts change every width, so measure again once they're in.
    document.fonts?.ready.then(() => fit(true));
    return () => observer.disconnect();
  }, [fit]);

  return (
    <Tag ref={ref} className={`min-w-0 ${className}`} style={{ fontSize: max, lineHeight }}>
      {content}
    </Tag>
  );
}
